import React, { useState } from 'react';
import {
  FiX, FiZap, FiAlertCircle, FiCheckCircle, FiRefreshCw,
  FiFileText, FiCode, FiAlertTriangle, FiInfo, FiArrowRight,
} from 'react-icons/fi';
import {
  generateIncrementalUpdate,
  type IncrementalUpdateReport,
} from '../../api/ai';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentNodes: any[];
  currentEdges: any[];
  previousNodes?: any[];
  previousEdges?: any[];
  provider: string;
  environment: string;
  architectureName?: string;
}

const IMPACT_CONFIG = {
  low:    { label: 'Faible',  badge: 'bg-blue-100 text-blue-700 border-blue-300' },
  medium: { label: 'Moyen',   badge: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  high:   { label: 'Élevé',   badge: 'bg-red-100 text-red-700 border-red-300' },
};

const AREA_ICONS: Record<string, React.ReactNode> = {
  documentation: <FiFileText size={12} />,
  terraform:     <FiCode size={12} />,
  security:      <FiAlertCircle size={12} />,
  cost:          <FiInfo size={12} />,
  resilience:    <FiCheckCircle size={12} />,
};

function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="bg-gray-900 text-green-300 text-xs rounded-lg p-3 overflow-x-auto font-mono whitespace-pre-wrap">
      {code}
    </pre>
  );
}

const IncrementalUpdateModal: React.FC<Props> = ({
  isOpen, onClose, currentNodes, currentEdges,
  previousNodes, previousEdges, provider, environment, architectureName,
}) => {
  const [step, setStep]                   = useState<'form' | 'result'>('form');
  const [changeDescription, setChangeDesc] = useState('');
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [update, setUpdate]               = useState<IncrementalUpdateReport | null>(null);
  const [metadata, setMetadata]           = useState<{ tokensUsed: number; durationMs: number } | null>(null);
  const [activeTab, setActiveTab]         = useState<'doc' | 'adr' | 'terraform'>('doc');

  // Compute simple diff counts for display
  const prevIds = new Set((previousNodes || []).map(n => n.id));
  const currIds = new Set(currentNodes.map(n => n.id));
  const addedCount   = currentNodes.filter(n => !prevIds.has(n.id) && n.data?.type).length;
  const removedCount = (previousNodes || []).filter(n => !currIds.has(n.id) && n.data?.type).length;
  const hasDiff = addedCount > 0 || removedCount > 0;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await generateIncrementalUpdate({
        currentNodes, currentEdges,
        previousNodes, previousEdges,
        provider, environment, architectureName,
        changeDescription: changeDescription.trim() || undefined,
      });
      setUpdate(result.update);
      setMetadata({ tokensUsed: result.metadata.tokensUsed, durationMs: result.metadata.durationMs });
      setStep('result');
      setActiveTab('doc');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = () => {
    if (!update) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const terraformSnippets = update.terraformUpdate.resourcesAdded
      .map(r => `<h4>${r.name} (${r.type})</h4><pre>${r.snippet?.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>`)
      .join('');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Mise à jour incrémentale — ${architectureName || provider}</title>
    <style>
      body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;color:#111;font-size:14px}
      h1{font-size:20px;font-weight:700;border-bottom:2px solid #4f46e5;padding-bottom:8px}
      h2{font-size:16px;font-weight:600;margin:20px 0 8px;color:#312e81}
      h3,h4{font-size:14px;font-weight:600;margin:12px 0 4px}
      pre{background:#1e1b4b;color:#c7d2fe;padding:12px;border-radius:6px;font-size:12px;overflow-x:auto}
      ul{margin:6px 0;padding-left:20px}li{margin:3px 0}
      .badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{background:#eef2ff;padding:8px 12px;text-align:left;font-size:12px;border:1px solid #c7d2fe}
      td{padding:8px 12px;font-size:12px;border:1px solid #e0e7ff}
      @media print{body{padding:16px}}
    </style></head><body>`);
    w.document.write(`<h1>Mise à jour incrémentale — ${architectureName || provider}</h1>`);
    w.document.write(`<p><b>Impact :</b> ${update.impact} | <b>Zones :</b> ${update.affectedAreas.join(', ')}</p>`);
    w.document.write(`<p>${update.summary}</p>`);
    w.document.write(`<h2>Changelog</h2><pre>${update.docUpdate.changeLog?.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>`);
    if (update.docUpdate.updatedSections?.length) {
      w.document.write(`<h2>Sections mises à jour</h2><table><tr><th>Section</th><th>Avant</th><th>Après</th><th>Raison</th></tr>`);
      update.docUpdate.updatedSections.forEach(s => {
        w.document.write(`<tr><td>${s.section}</td><td>${s.before}</td><td>${s.after}</td><td>${s.reason}</td></tr>`);
      });
      w.document.write('</table>');
    }
    if (update.adrUpdate.newDecision) {
      w.document.write(`<h2>Nouvelle décision ADR : ${update.adrUpdate.newDecision}</h2>`);
      w.document.write(`<pre>${update.adrUpdate.content?.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>`);
    }
    w.document.write(`<h2>Terraform</h2>`);
    if (update.terraformUpdate.resourcesAdded.length) { w.document.write(`<h3>Ressources ajoutées</h3>${terraformSnippets}`); }
    if (update.terraformUpdate.resourcesRemoved.length) { w.document.write(`<h3>À supprimer</h3><ul>${update.terraformUpdate.resourcesRemoved.map(r=>`<li>${r}</li>`).join('')}</ul>`); }
    if (update.recommendations.length) { w.document.write(`<h2>Recommandations</h2><ul>${update.recommendations.map(r=>`<li>${r}</li>`).join('')}</ul>`); }
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  if (!isOpen) return null;

  const impactCfg = update ? (IMPACT_CONFIG[update.impact] || IMPACT_CONFIG.medium) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FiZap className="text-amber-500" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">Mise à jour incrémentale</h2>
            {hasDiff && (
              <span className="text-xs text-gray-400 font-normal">
                {addedCount > 0 && <span className="text-green-600">+{addedCount}</span>}
                {removedCount > 0 && <span className="text-red-500 ml-1">-{removedCount}</span>}
                {' '}ressource{addedCount + removedCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 'result' && (
              <button onClick={() => { setStep('form'); setUpdate(null); setError(null); }}
                className="text-xs text-gray-500 hover:text-amber-600 transition-colors px-2 py-1 rounded hover:bg-gray-50">
                ← Relancer
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Step 1: Form */}
          {step === 'form' && (
            <div className="space-y-5 max-w-lg mx-auto">
              <p className="text-sm text-gray-600">
                L'IA analyse les changements de l'architecture et génère les mises à jour de documentation, ADR et Terraform correspondantes.
              </p>

              {/* Diff summary */}
              <div className={`rounded-xl p-4 border ${hasDiff ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                <p className="text-xs font-semibold text-gray-600 mb-2">Changements détectés</p>
                {hasDiff ? (
                  <div className="flex gap-4">
                    {addedCount > 0 && (
                      <div className="flex items-center gap-1.5 text-green-700">
                        <FiCheckCircle size={13} />
                        <span className="text-sm font-medium">{addedCount} ajouté{addedCount > 1 ? 's' : ''}</span>
                      </div>
                    )}
                    {removedCount > 0 && (
                      <div className="flex items-center gap-1.5 text-red-600">
                        <FiAlertTriangle size={13} />
                        <span className="text-sm font-medium">{removedCount} supprimé{removedCount > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucune snapshot précédente — analyse de l'état actuel.</p>
                )}
              </div>

              {/* Optional description */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Description du changement (optionnel)
                </label>
                <textarea
                  value={changeDescription}
                  onChange={e => setChangeDesc(e.target.value)}
                  rows={3}
                  placeholder="ex : Ajout d'un cache Redis pour améliorer les performances, suppression du service legacy..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 resize-none"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
                  <FiAlertCircle size={14} />{error}
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{ background: loading ? '#9ca3af' : 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                {loading
                  ? <><FiRefreshCw size={15} className="animate-spin" /> Analyse en cours…</>
                  : <><FiZap size={15} /> Générer les mises à jour</>}
              </button>
            </div>
          )}

          {/* Step 2: Result */}
          {step === 'result' && update && (
            <div className="space-y-5">

              {/* Summary */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm text-gray-700 mb-2">{update.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    {impactCfg && (
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${impactCfg.badge}`}>
                        Impact : {impactCfg.label}
                      </span>
                    )}
                    {update.affectedAreas.map(area => (
                      <span key={area} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-600">
                        {AREA_ICONS[area] || <FiInfo size={11} />}{area}
                      </span>
                    ))}
                  </div>
                </div>
                {update.recommendations?.length > 0 && (
                  <div className="border-l border-amber-200 pl-4 min-w-[180px]">
                    <p className="text-xs font-semibold text-gray-600 mb-1.5">Recommandations</p>
                    {update.recommendations.map((r, i) => (
                      <div key={i} className="flex items-start gap-1.5 mb-1">
                        <FiArrowRight size={11} className="text-amber-500 mt-0.5 shrink-0" />
                        <span className="text-xs text-gray-600">{r}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-gray-200">
                {([
                  { key: 'doc',      label: 'Documentation',  icon: <FiFileText size={13} /> },
                  { key: 'adr',      label: 'ADR',            icon: <FiFileText size={13} /> },
                  { key: 'terraform', label: 'Terraform',      icon: <FiCode size={13} /> },
                ] as const).map(tab => (
                  <button key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2.5 border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}>
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>

              {/* Doc tab */}
              {activeTab === 'doc' && (
                <div className="space-y-4">
                  {update.docUpdate.changeLog && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Changelog à ajouter</p>
                      <CodeBlock code={update.docUpdate.changeLog} />
                    </div>
                  )}
                  {update.docUpdate.updatedSections?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Sections à mettre à jour</p>
                      <div className="space-y-2">
                        {update.docUpdate.updatedSections.map((s, i) => (
                          <div key={i} className="border border-gray-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">{s.section}</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-red-50 rounded p-2">
                                <p className="text-[10px] text-red-500 font-semibold mb-1">Avant</p>
                                <p className="text-xs text-gray-600">{s.before}</p>
                              </div>
                              <div className="bg-green-50 rounded p-2">
                                <p className="text-[10px] text-green-600 font-semibold mb-1">Après</p>
                                <p className="text-xs text-gray-700">{s.after}</p>
                              </div>
                            </div>
                            {s.reason && (
                              <p className="text-[10px] text-gray-500 mt-1.5">→ {s.reason}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ADR tab */}
              {activeTab === 'adr' && (
                <div className="space-y-4">
                  {update.adrUpdate.newDecision ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-600">Nouvelle ADR :</span>
                        <span className="text-xs font-medium text-indigo-600">{update.adrUpdate.newDecision}</span>
                      </div>
                      {update.adrUpdate.content && <CodeBlock code={update.adrUpdate.content} />}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <FiInfo size={14} />
                      <span className="text-sm">Aucune nouvelle ADR requise pour ce changement.</span>
                    </div>
                  )}
                  {update.adrUpdate.updatedDecisions?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">ADR existantes à réviser</p>
                      <ul className="space-y-1">
                        {update.adrUpdate.updatedDecisions.map((d, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                            <FiArrowRight size={11} className="text-indigo-400 mt-0.5 shrink-0" />{d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Terraform tab */}
              {activeTab === 'terraform' && (
                <div className="space-y-4">
                  {update.terraformUpdate.resourcesAdded?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1.5">
                        <FiCheckCircle size={12} /> Ressources à ajouter
                      </p>
                      {update.terraformUpdate.resourcesAdded.map((r, i) => (
                        <div key={i} className="mb-3">
                          <p className="text-xs text-gray-500 mb-1 font-mono">{r.type}.{r.name}</p>
                          {r.snippet && <CodeBlock code={r.snippet} />}
                        </div>
                      ))}
                    </div>
                  )}
                  {update.terraformUpdate.resourcesRemoved?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-red-600 mb-2 flex items-center gap-1.5">
                        <FiAlertTriangle size={12} /> Ressources à supprimer
                      </p>
                      <ul className="space-y-1">
                        {update.terraformUpdate.resourcesRemoved.map((r, i) => (
                          <li key={i} className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                            terraform destroy -target={r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {update.terraformUpdate.resourcesModified?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-yellow-700 mb-2 flex items-center gap-1.5">
                        <FiRefreshCw size={12} /> Ressources à modifier
                      </p>
                      {update.terraformUpdate.resourcesModified.map((r, i) => (
                        <div key={i} className="border border-yellow-200 bg-yellow-50 rounded-lg p-2.5 mb-2">
                          <p className="text-xs font-mono font-semibold text-yellow-800 mb-1">{r.name}</p>
                          <ul className="space-y-0.5">
                            {r.changes.map((c, j) => (
                              <li key={j} className="text-xs text-yellow-700">→ {c}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  {update.terraformUpdate.variablesUpdated?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Variables terraform.tfvars</p>
                      <ul className="space-y-1">
                        {update.terraformUpdate.variablesUpdated.map((v, i) => (
                          <li key={i} className="text-xs font-mono text-gray-600 bg-gray-100 rounded px-2 py-1">{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {update.terraformUpdate.resourcesAdded.length === 0 && update.terraformUpdate.resourcesRemoved.length === 0 && update.terraformUpdate.resourcesModified.length === 0 && (
                    <div className="flex items-center gap-2 text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <FiCheckCircle size={14} className="text-green-500" />
                      <span className="text-sm">Aucun changement Terraform nécessaire.</span>
                    </div>
                  )}
                  {update.rollbackSteps?.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-orange-700 mb-1.5">Rollback</p>
                      <ul className="space-y-1">
                        {update.rollbackSteps.map((s, i) => (
                          <li key={i} className="text-xs text-orange-700 flex items-start gap-1.5">
                            <span className="font-bold shrink-0">{i + 1}.</span>{s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'result' && update && (
          <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between gap-4 bg-gray-50 rounded-b-2xl">
            <div className="text-xs text-gray-400 flex items-center gap-3">
              {metadata && (
                <>
                  <span>{metadata.tokensUsed.toLocaleString()} tokens</span>
                  <span>{(metadata.durationMs / 1000).toFixed(1)}s</span>
                </>
              )}
            </div>
            <button onClick={handleExportPdf}
              className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-amber-600 transition-colors px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-amber-300">
              Export PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncrementalUpdateModal;
